<script setup>
import { ref, computed, watch, onMounted, nextTick } from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { debounce } from '@chatwoot/utils';
import { EditorState } from '@codemirror/state';
import { EditorView, Decoration, WidgetType } from '@codemirror/view';
import { StateField, StateEffect } from '@codemirror/state';
import { basicSetup } from 'codemirror';
import { json } from '@codemirror/lang-json';
import Icon from 'next/icon/Icon.vue';
import SchemaFieldItem from './SchemaFieldItem.vue';
import LoadingIndicator from '@/shared/components/LoadingIndicator.vue';
import { isImmutableSchemaKnowledgebase } from './composables/useImmutableSchemaKnowledgebase';

const props = defineProps({
  knowledgebaseId: {
    type: String,
    required: true,
  },
});

const store = useStore();
const router = useRouter();
const route = useRoute();
const { t } = useI18n();
const editableSchema = ref('');
const editorView = ref(null);
const editorContainer = ref(null);
const errorMessage = ref(null);
const delayedLoading = ref(false);
const isUpdatingFromStore = ref(false);
const isTyping = ref(false);
const uiFlags = computed(() => store.getters['knowledgebase/getUIFlags']);
const selectedKnowledgebase = computed(
  () => store.getters['knowledgebase/getSelectedLibrary']
);
const matchingKnowledgebase = computed(() => {
  const knowledgebase = selectedKnowledgebase.value;
  if (
    !knowledgebase ||
    String(knowledgebase.id) !== String(props.knowledgebaseId)
  ) {
    return null;
  }
  return knowledgebase;
});
const schemaEditingDisabled = computed(() =>
  isImmutableSchemaKnowledgebase(matchingKnowledgebase.value)
);
const canShowSchemaEditor = computed(
  () => matchingKnowledgebase.value && !schemaEditingDisabled.value
);
const activeSchema = computed(
  () => store.getters['knowledgebase/getActiveSchema']
);

const isLoading = computed(
  () => uiFlags.value.isUpdatingSchema || isTyping.value
);

const jsonValidation = ref({ isValid: true, error: null });

const validateJSON = jsonString => {
  try {
    JSON.parse(jsonString);
    return { isValid: true, error: null };
  } catch (e) {
    // Extract line and column information from the error message
    const match = e.message.match(/at position (\d+)/);
    let lineInfo = '';
    let lineNumber = null;
    let columnNumber = null;

    if (match) {
      const position = parseInt(match[1], 10);
      const textBeforeError = jsonString.substring(0, position);
      lineNumber = textBeforeError.split('\n').length;
      columnNumber = textBeforeError.split('\n').pop().length + 1;
      lineInfo = ` (line ${lineNumber}, column ${columnNumber})`;
    }

    return {
      isValid: false,
      error: `${e.message}${lineInfo}`,
      lineNumber,
      columnNumber,
    };
  }
};

const debouncedValidateJSON = debounce(jsonString => {
  jsonValidation.value = validateJSON(jsonString);
}, 1000);

// Effect for updating error decorations
const updateErrorDecorations = StateEffect.define();

// StateField to manage error line decorations
const errorDecorations = StateField.define({
  create() {
    return Decoration.none;
  },
  update(decorations, tr) {
    decorations = decorations.map(tr.changes);
    const updateEffect = tr.effects.find(effect =>
      effect.is(updateErrorDecorations)
    );
    if (updateEffect) {
      decorations = updateEffect.value;
    }
    return decorations;
  },
  provide: f => EditorView.decorations.from(f),
});

// Decoration for error lines
const errorLineDeco = Decoration.line({
  class: 'cm-error-line',
});

// Widget class for inline error messages
class ErrorWidget extends WidgetType {
  constructor(message) {
    super();
    this.message = message;
  }

  toDOM() {
    const wrap = document.createElement('div');
    wrap.className = 'cm-error-widget';
    wrap.innerHTML = `
      <div class="cm-error-message">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"/>
          <line x1="15" y1="9" x2="9" y2="15"/>
          <line x1="9" y1="9" x2="15" y2="15"/>
        </svg>
        <span>${this.message}</span>
      </div>
    `;
    return wrap;
  }

  static ignoreEvent() {
    return false;
  }
}

watch(
  () => activeSchema.value,
  newSchema => {
    // Only update editor if we're not currently updating from local changes
    if (isUpdatingFromStore.value) return;

    const newSchemaString = JSON.stringify(newSchema, null, 2);
    if (newSchemaString !== editableSchema.value) {
      editableSchema.value = newSchemaString;
      // Clear typing state when schema is loaded from store
      isTyping.value = false;
      // Immediately validate when schema is loaded from store
      jsonValidation.value = validateJSON(newSchemaString);
      if (editorView.value) {
        editorView.value.dispatch({
          changes: {
            from: 0,
            to: editorView.value.state.doc.length,
            insert: newSchemaString,
          },
        });
      }
    }
  },
  { immediate: true }
);

watch(
  () => props.knowledgebaseId,
  newId => {
    if (!newId) return;
    if (String(newId) === String(selectedKnowledgebase.value?.id)) return;

    if (editorView.value) {
      editorView.value.destroy();
      editorView.value = null;
    }

    store.dispatch('knowledgebase/getSingleLibrary', newId);
    store.dispatch('knowledgebase/clearActiveSchema');
  }
);

let loadingTimeout = null;

watch(
  () => isLoading.value,
  newValue => {
    if (loadingTimeout) {
      clearTimeout(loadingTimeout);
    }

    if (newValue) {
      delayedLoading.value = true;
    } else {
      loadingTimeout = setTimeout(() => {
        delayedLoading.value = false;
      }, 300);
    }
  }
);

const updateErrorHighlighting = () => {
  if (!editorView.value) return;

  const validation = jsonValidation.value;
  let decorations = Decoration.none;

  if (!validation.isValid && validation.lineNumber) {
    const doc = editorView.value.state.doc;
    const line = doc.line(validation.lineNumber);
    const linePos = line.from;
    const lineEnd = line.to;

    // Create both line decoration and error widget
    const errorWidget = Decoration.widget({
      widget: new ErrorWidget(validation.error),
      side: 1,
      block: true,
    });

    decorations = Decoration.set([
      errorLineDeco.range(linePos),
      errorWidget.range(lineEnd),
    ]);
  }

  editorView.value.dispatch({
    effects: updateErrorDecorations.of(decorations),
  });
};

// Watch for editableSchema changes and trigger debounced validation
watch(
  () => editableSchema.value,
  newValue => {
    debouncedValidateJSON(newValue);
  }
);

// Watch for JSON validation changes and update highlighting
watch(
  () => jsonValidation.value,
  () => {
    updateErrorHighlighting();
  }
);

const redirectToContent = () => {
  router.replace({
    name: 'settings_knowledgebases_content',
    params: {
      accountId: route.params.accountId,
      knowledgebaseId: route.params.knowledgebaseId,
    },
  });
};

watch(
  matchingKnowledgebase,
  knowledgebase => {
    if (knowledgebase && schemaEditingDisabled.value) {
      redirectToContent();
    }
  },
  { immediate: true }
);

const initializeEditor = () => {
  if (!editorContainer.value || editorView.value) return;

  // Initialize JSON validation
  if (editableSchema.value) {
    jsonValidation.value = validateJSON(editableSchema.value);
  }

  const state = EditorState.create({
    doc: editableSchema.value,
    extensions: [
      basicSetup,
      json(),
      errorDecorations,
      EditorView.updateListener.of(update => {
        if (update.docChanged) {
          editableSchema.value = update.state.doc.toString();
        }
      }),
      EditorView.theme({
        '&': {
          height: '100%',
          maxHeight: '100%',
          outline: 'none',
        },
        '&.cm-focused': {
          outline: 'none',
        },
        '.cm-scroller': {
          height: '100%',
          maxHeight: '100%',
          overflow: 'auto',
        },
        '.cm-content': {
          minHeight: '0',
        },
        '.cm-error-line': {
          backgroundColor: '#fef2f2',
          borderLeft: '4px solid #ef4444',
        },
        '.cm-error-widget': {
          padding: '8px 12px',
          margin: '4px 0',
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '6px',
          fontSize: '14px',
          lineHeight: '1.4',
        },
        '.cm-error-message': {
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          color: '#dc2626',
          fontFamily:
            'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace',
        },
        '.cm-error-message svg': {
          flexShrink: '0',
          width: '16px',
          height: '16px',
        },
      }),
    ],
  });

  if (!editorView.value) {
    editorView.value = new EditorView({
      state,
      parent: editorContainer.value,
    });

    // Add resize observer to handle container size changes
    const resizeObserver = new ResizeObserver(() => {
      if (editorView.value) {
        editorView.value.requestMeasure();
      }
    });
    resizeObserver.observe(editorContainer.value);

    // Initial error highlighting
    updateErrorHighlighting();
  }
};

watch(
  canShowSchemaEditor,
  async canShow => {
    if (!canShow) return;

    if (!activeSchema.value) {
      store.dispatch('knowledgebase/fetchActiveSchema', props.knowledgebaseId);
    }

    await nextTick();
    initializeEditor();
  },
  { immediate: true }
);

onMounted(() => {
  if (!matchingKnowledgebase.value) {
    store.dispatch('knowledgebase/getSingleLibrary', props.knowledgebaseId);
  }
});

const showError = message => {
  errorMessage.value = message;
};

// Parse error to check if it's the missing required properties error
const parsedError = computed(() => {
  if (!errorMessage.value) return null;

  let errorObj = null;
  let errorText = '';

  // Extract error text regardless of format
  if (typeof errorMessage.value === 'string') {
    errorText = errorMessage.value;
    try {
      // Try to parse as JSON first
      errorObj = JSON.parse(errorMessage.value);
    } catch (e) {
      // Not JSON, use string directly
    }
  } else if (typeof errorMessage.value === 'object') {
    // Handle if error is already an object
    errorObj = errorMessage.value;
    errorText = errorObj.message || errorObj.error || JSON.stringify(errorObj);
  }

  // Check for missing title and type in various ways
  const isMissingTitleType =
    errorText.includes('Missing required properties: title, type') ||
    errorText.includes('missing_properties') ||
    (errorText.includes('title') &&
      errorText.includes('type') &&
      errorText.includes('required')) ||
    (errorText.includes('title') &&
      errorText.includes('type') &&
      errorText.includes('missing'));

  if (isMissingTitleType) {
    return {
      key: 'missing_properties',
      error: errorText.includes('Missing required properties: title, type')
        ? 'Missing required properties: title, type'
        : errorText,
      details: ['title', 'type'],
    };
  }

  // Also check structured error object
  if (
    errorObj &&
    (errorObj.key === 'missing_properties' ||
      (errorObj.error &&
        errorObj.error.includes('Missing required properties: title, type'))) &&
    errorObj.details &&
    Array.isArray(errorObj.details) &&
    errorObj.details.includes('title') &&
    errorObj.details.includes('type')
  ) {
    return errorObj;
  }

  return null;
});

// Check if this is a fixable missing properties error
const canFixMissingProperties = computed(() => {
  return parsedError.value !== null;
});

// Fix the missing properties error
const fixMissingProperties = () => {
  try {
    const currentSchema = JSON.parse(editableSchema.value);

    // Add title and type to top level if they don't exist
    // Use knowledgebase title and description as defaults
    if (!currentSchema.title) {
      // Process title: remove spaces and special chars, convert to snake_case
      const rawTitle = selectedKnowledgebase.value?.title || 'schema_title';
      const processedTitle = rawTitle
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
      currentSchema.title = processedTitle || 'schema_title';
    }
    if (!currentSchema.description) {
      currentSchema.description =
        selectedKnowledgebase.value?.description || '';
    }
    if (!currentSchema.type) {
      currentSchema.type = 'object';
    }

    // Update editor content
    const newSchemaString = JSON.stringify(currentSchema, null, 2);
    editableSchema.value = newSchemaString;

    if (editorView.value) {
      editorView.value.dispatch({
        changes: {
          from: 0,
          to: editorView.value.state.doc.length,
          insert: newSchemaString,
        },
      });
    }

    // Clear the error since we fixed it
    errorMessage.value = null;
  } catch (error) {
    showError('Failed to fix schema: Invalid JSON');
  }
};

const debouncedSaveSchema = debounce(async () => {
  const validation = jsonValidation.value;
  if (!validation.isValid) {
    isTyping.value = false;
    return;
  }

  const updatedSchema = JSON.parse(editableSchema.value);

  try {
    // Set flag to prevent watch from updating editor during save
    isUpdatingFromStore.value = true;

    await store.dispatch('knowledgebase/updateActiveSchema', {
      id: selectedKnowledgebase.value.id,
      schema: updatedSchema,
    });

    // Clear error only after successful save
    errorMessage.value = null;
  } catch (error) {
    showError(error.message || error);
  } finally {
    // Reset flags after save operation completes
    isTyping.value = false;
    setTimeout(() => {
      isUpdatingFromStore.value = false;
    }, 100);
  }
}, 2000);

// Template functions for adding schema fields
const insertPropertyToSchema = newProperty => {
  try {
    const currentSchema = JSON.parse(editableSchema.value);

    // Ensure properties object exists at the root level
    if (!currentSchema.properties) {
      currentSchema.properties = {};
    }

    Object.assign(currentSchema.properties, newProperty);

    const newSchemaString = JSON.stringify(currentSchema, null, 2);
    editableSchema.value = newSchemaString;

    if (editorView.value) {
      editorView.value.dispatch({
        changes: {
          from: 0,
          to: editorView.value.state.doc.length,
          insert: newSchemaString,
        },
      });
    }
  } catch (error) {
    showError('Failed to add property: Invalid JSON schema');
  }
};

const addVenueLocationField = () => {
  if (!editorView.value) return;

  const locationProperty = {
    location: {
      type: 'geo',
      description:
        'Geographic location of the venue (latitude and longitude coordinates)',
    },
  };

  insertPropertyToSchema(locationProperty);
};

const addVenueCategoryField = () => {
  if (!editorView.value) return;

  const categoryProperty = {
    space: {
      type: 'keyword',
      description:
        'Category or type of venue space (e.g., restaurant, cafe, hotel, museum)',
    },
  };

  insertPropertyToSchema(categoryProperty);
};

// Standard JSON Schema type functions
const addTextField = () => {
  if (!editorView.value) return;

  const textProperty = {
    title: {
      type: 'text',
      description: 'Full-text searchable string field',
    },
  };

  insertPropertyToSchema(textProperty);
};

const addIntegerField = () => {
  if (!editorView.value) return;

  const integerProperty = {
    count: {
      type: 'integer',
      description: 'Whole number field for numeric data',
    },
  };

  insertPropertyToSchema(integerProperty);
};

const addNumberField = () => {
  if (!editorView.value) return;

  const numberProperty = {
    price: {
      type: 'float',
      description: 'Decimal number field for precise numeric values',
    },
  };

  insertPropertyToSchema(numberProperty);
};

const addBooleanField = () => {
  if (!editorView.value) return;

  const booleanProperty = {
    active: {
      type: 'boolean',
      description: 'True or false value field',
    },
  };

  insertPropertyToSchema(booleanProperty);
};

const addDateField = () => {
  if (!editorView.value) return;

  const dateProperty = {
    created_at: {
      type: 'date',
      description: 'Date and time field with ISO format',
    },
  };

  insertPropertyToSchema(dateProperty);
};

const addLocationField = () => {
  if (!editorView.value) return;

  const locationProperty = {
    location: {
      type: 'geo',
      description: 'Geographic location field with latitude and longitude',
    },
  };

  insertPropertyToSchema(locationProperty);
};

watch(editableSchema, () => {
  // Set typing state immediately when user makes changes
  if (!isUpdatingFromStore.value) {
    isTyping.value = true;
  }
  debouncedSaveSchema();
});
</script>

<template>
  <div v-if="canShowSchemaEditor" class="flex w-full h-full">
    <!-- Main Editor Area -->
    <div class="flex-1 relative">
      <div
        ref="editorContainer"
        data-testid="schema-editor-container"
        class="absolute inset-0"
      />
      <LoadingIndicator
        v-if="delayedLoading"
        :message="t('LIBRARIES.DOCUMENTS.SCHEMA.SAVING')"
        position="absolute"
        placement="bottom-right"
        size="sm"
        variant="primary"
        :z-index="10"
      />
    </div>

    <!-- Sidebar -->
    <div
      class="schema-sidebar sticky top-8 m-8 w-80 border-2 border-l border-n-slate-3 dark:border-n-slate-8 rounded-lg flex flex-col bg-white dark:bg-slate-900"
      data-testid="schema-sidebar"
    >
      <!-- Header -->
      <div class="p-4 border-b border-n-slate-3 dark:border-n-slate-8">
        <h2 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
          {{ t('LIBRARIES.DOCUMENTS.SCHEMA.SIDEBAR.TITLE') }}
        </h2>
      </div>

      <!-- Content -->
      <div class="flex-1 overflow-y-auto p-4 space-y-6">
        <!-- Tips/Error Section with Reserved Height -->
        <div class="h-32 flex flex-col">
          <div
            v-if="errorMessage"
            class="flex-1 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3"
          >
            <h4
              class="text-sm font-medium text-red-800 dark:text-red-200 mb-2 flex items-center gap-2"
            >
              <Icon icon="i-lucide-alert-triangle" class="w-4 h-4" />
              {{ t('LIBRARIES.DOCUMENTS.SCHEMA.SIDEBAR.SAVE_ERRORS') }}
            </h4>
            <div class="flex items-start justify-between gap-2">
              <p
                class="text-xs text-red-700 dark:text-red-300 overflow-y-auto flex-1"
              >
                {{ parsedError ? parsedError.error : errorMessage }}
              </p>
              <button
                v-if="canFixMissingProperties"
                class="flex-shrink-0 inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-800 dark:text-red-200 bg-red-100 dark:bg-red-800 border border-red-300 dark:border-red-600 rounded hover:bg-red-200 dark:hover:bg-red-700 transition-colors"
                @click="fixMissingProperties"
              >
                <Icon icon="i-lucide-wrench" class="w-3 h-3" />
                {{ t('LIBRARIES.DOCUMENTS.SCHEMA.SIDEBAR.FIX_SCHEMA') }}
              </button>
            </div>
          </div>
          <div
            v-else
            class="flex-1 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3"
          >
            <h4
              class="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2 flex items-center gap-2"
            >
              <Icon icon="i-lucide-lightbulb" class="w-4 h-4" />
              {{ t('LIBRARIES.DOCUMENTS.SCHEMA.SIDEBAR.TIPS') }}
            </h4>
            <p class="text-xs text-blue-700 dark:text-blue-300">
              {{ t('LIBRARIES.DOCUMENTS.SCHEMA.SIDEBAR.FILTER_TIP') }}
            </p>
          </div>
        </div>

        <!-- Properties Section -->
        <div>
          <h3
            class="text-md font-medium text-slate-900 dark:text-slate-100 mb-3"
          >
            {{ t('LIBRARIES.DOCUMENTS.SCHEMA.SIDEBAR.PROPERTIES') }}
          </h3>

          <!-- Venues Category -->
          <div class="mb-4">
            <h4
              class="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 flex items-center gap-2"
            >
              <Icon icon="i-lucide-map-pin" class="w-3 h-3" />
              {{ t('LIBRARIES.DOCUMENTS.SCHEMA.SIDEBAR.VENUES') }}
            </h4>
            <div class="flex flex-wrap gap-1">
              <SchemaFieldItem
                field-name="location"
                field-type="geo"
                :description="
                  t('LIBRARIES.DOCUMENTS.SCHEMA.SIDEBAR.LOCATION_DESC')
                "
                icon="i-lucide-map-pin"
                @click="addVenueLocationField"
              />

              <SchemaFieldItem
                field-name="space"
                field-type="keyword"
                :description="
                  t('LIBRARIES.DOCUMENTS.SCHEMA.SIDEBAR.CATEGORY_DESC')
                "
                icon="i-lucide-tag"
                @click="addVenueCategoryField"
              />
            </div>
          </div>

          <!-- Standard Types Category -->
          <div class="mb-4">
            <h4
              class="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 flex items-center gap-2"
            >
              <Icon icon="i-lucide-type" class="w-3 h-3" />
              {{ t('LIBRARIES.DOCUMENTS.SCHEMA.SIDEBAR.STANDARD_TYPES') }}
            </h4>
            <div class="flex flex-wrap gap-1">
              <SchemaFieldItem
                field-name="title"
                field-type="text"
                :description="t('LIBRARIES.DOCUMENTS.SCHEMA.SIDEBAR.TEXT_DESC')"
                icon="i-lucide-type"
                @click="addTextField"
              />

              <SchemaFieldItem
                field-name="count"
                field-type="integer"
                :description="
                  t('LIBRARIES.DOCUMENTS.SCHEMA.SIDEBAR.INTEGER_DESC')
                "
                icon="i-lucide-hash"
                @click="addIntegerField"
              />

              <SchemaFieldItem
                field-name="price"
                field-type="float"
                :description="
                  t('LIBRARIES.DOCUMENTS.SCHEMA.SIDEBAR.FLOAT_DESC')
                "
                icon="i-lucide-calculator"
                @click="addNumberField"
              />

              <SchemaFieldItem
                field-name="active"
                field-type="boolean"
                :description="
                  t('LIBRARIES.DOCUMENTS.SCHEMA.SIDEBAR.BOOLEAN_DESC')
                "
                icon="i-lucide-toggle-left"
                @click="addBooleanField"
              />

              <SchemaFieldItem
                field-name="created_at"
                field-type="date"
                :description="t('LIBRARIES.DOCUMENTS.SCHEMA.SIDEBAR.DATE_DESC')"
                icon="i-lucide-calendar"
                @click="addDateField"
              />

              <SchemaFieldItem
                field-name="location"
                field-type="geo"
                :description="
                  t('LIBRARIES.DOCUMENTS.SCHEMA.SIDEBAR.LOCATION_DESC')
                "
                icon="i-lucide-map-pin"
                @click="addLocationField"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
